import { makeVar, useReactiveVar } from '@apollo/client';
import {
  sfuScreenShareBridge,
  liveKitScreenShareBridge,
} from '/imports/api/screenshare/client/bridge';
import BridgeService from '/imports/api/screenshare/client/bridge/service';
import logger from '/imports/startup/client/logger';
import AudioService from '/imports/ui/components/audio/service';
import MediaStreamUtils from '/imports/utils/media-stream-utils';
import ConnectionStatusService from '/imports/ui/components/connection-status/service';
import browserInfo from '/imports/utils/browserInfo';
import createUseSubscription from '/imports/ui/core/hooks/createUseSubscription';
import { SCREENSHARE_SUBSCRIPTION } from './queries';

let screenShareBridge = sfuScreenShareBridge;

export const setBridge = (bridgeName) => {
  switch (bridgeName) {
    case 'bbb-webrtc-sfu':
      screenShareBridge = sfuScreenShareBridge;
      break;
    case 'livekit':
      screenShareBridge = liveKitScreenShareBridge;
      break;
    default:
      logger.warn({
        logCode: 'screenshare_unknown_bridge',
        extraInfo: {
          targetBridge: bridgeName,
        },
      }, `Unknown screenshare bridge: ${bridgeName}`);
      // Hardcoded default is bbb-webrtc-sfu
      screenShareBridge = sfuScreenShareBridge;
      break;
  }

  logger.debug({
    logCode: 'screenshare_bridge_set',
    extraInfo: {
      targetBridge: bridgeName,
      bridge: screenShareBridge?.bridgeName,
    },
  }, `Screenshare bridge set to ${screenShareBridge?.bridgeName}`);

  return screenShareBridge;
};

export const SCREENSHARE_MEDIA_ELEMENT_NAME = 'screenshareVideo';

export const DEFAULT_SCREENSHARE_STATS_TYPES = [
  'outbound-rtp',
  'inbound-rtp',
];

export const CONTENT_TYPE_CAMERA = 'camera';
export const CONTENT_TYPE_SCREENSHARE = 'screenshare';

export const isSharingVar = makeVar(false);
export const sharingContentTypeVar = makeVar(false);
export const cameraAsContentDeviceIdTypeVar = makeVar('');

export const useScreenshare = createUseSubscription(SCREENSHARE_SUBSCRIPTION, {}, true);

export const useIsSharing = () => useReactiveVar(isSharingVar);
export const useSharingContentType = () => useReactiveVar(sharingContentTypeVar);
export const useCameraAsContentDeviceIdType = () => useReactiveVar(cameraAsContentDeviceIdTypeVar);

export const isSharing = () => isSharingVar();

export const setIsSharing = (sharing) => {
  const previous = isSharing();
  if (previous !== sharing) {
    logger.info({
      logCode: 'screenshare_sharing_state_change',
      extraInfo: { previous, next: sharing },
    }, `[SCREENSHARE DEBUG] isSharingVar changing: ${previous} → ${sharing}`);
    isSharingVar(sharing);
  }
};

export const getSharingContentType = () => sharingContentTypeVar();

export const setSharingContentType = (contentType) => {
  if (getSharingContentType() !== contentType) {
    sharingContentTypeVar(contentType);
  }
};

export const getCameraAsContentDeviceId = () => cameraAsContentDeviceIdTypeVar();

export const setCameraAsContentDeviceId = (deviceId) => {
  if (getCameraAsContentDeviceId() !== deviceId) {
    cameraAsContentDeviceIdTypeVar(deviceId);
  }
};

export const _trackStreamTermination = (stream, handler) => {
  if (typeof stream !== 'object' || typeof handler !== 'function') {
    throw new TypeError('Invalid trackStreamTermination arguments');
  }
  let _handler = handler;

  // Dirty, but effective way of checking whether the browser supports the 'inactive'
  // event. If the oninactive interface is null, it can be overridden === supported.
  // If undefined, it's not; so we fallback to the track 'ended' event.
  // The track ended listener should probably be reviewed once we create
  // thin wrapper classes for MediaStreamTracks as well, because we'll want a single
  // media stream holding multiple tracks in the future
  if (stream.oninactive !== undefined) {
    if (typeof stream.oninactive === 'function') {
      const oldHandler = stream.oninactive;
      _handler = () => {
        oldHandler();
        handler();
      };
    }
    // [Fix N12] Use _handler (which chains old + new) instead of raw handler
    stream.addEventListener('inactive', _handler, { once: true });
  } else {
    const track = MediaStreamUtils.getVideoTracks(stream)[0];
    if (track) {
      // [Fix #6] Chain existing onended handler if present, then use
      // addEventListener with the chained handler only (not both
      // addEventListener + onended, which caused handler to fire twice).
      if (typeof track.onended === 'function') {
        const oldHandler = track.onended;
        _handler = () => {
          oldHandler();
          handler();
        };
      }
      track.onended = null; // Clear property handler — we use addEventListener only
      track.addEventListener('ended', _handler, { once: true });
    }
  }
};

export const _isStreamActive = (stream) => {
  const tracksAreActive = !stream.getTracks().some((track) => track.readyState === 'ended');

  return tracksAreActive && stream.active;
};

export const useIsScreenGloballyBroadcasting = () => {
  const { data, loading } = useScreenshare();

  return {
    screenIsShared: Boolean(
      data
    && data[0]
    && data[0].contentType === CONTENT_TYPE_SCREENSHARE
    && data[0].stream,
    ),
    loading,
  };
};

export const useIsCameraAsContentGloballyBroadcasting = () => {
  const { data } = useScreenshare();

  return Boolean(data && data[0] && data[0].contentType === CONTENT_TYPE_CAMERA && data[0].stream);
};

export const useIsScreenBroadcasting = () => {
  const active = useIsSharing();
  const sharingContentType = useSharingContentType();
  const { data } = useScreenshare();
  const sharing = active && sharingContentType === CONTENT_TYPE_SCREENSHARE;
  const screenIsShared = Boolean(
    data && data[0] && data[0].contentType === CONTENT_TYPE_SCREENSHARE && data[0].stream,
  );

  return sharing || screenIsShared;
};

export const useIsCameraAsContentBroadcasting = () => {
  const active = useIsSharing();
  const sharingContentType = useSharingContentType();
  const sharing = active && sharingContentType === CONTENT_TYPE_CAMERA;
  const cameraAsContentIsShared = useIsCameraAsContentGloballyBroadcasting();

  return sharing || cameraAsContentIsShared;
};

export const useScreenshareHasAudio = () => {
  const { data } = useScreenshare();

  return Boolean(data && data[0] && data[0].hasAudio);
};

export const useBroadcastContentType = () => {
  const { data } = useScreenshare();

  if (!data || !data[0]) {
    // defaults to contentType: "camera"
    return CONTENT_TYPE_CAMERA;
  }

  return data[0].contentType;
};

export const useScreenshareStreamId = () => {
  const { data } = useScreenshare();

  return data?.[0]?.stream;
};

export const screenshareHasEnded = () => {
  const wasSharing = isSharingVar();
  const contentType = getSharingContentType();
  logger.info({
    logCode: 'screenshare_has_ended_called',
    extraInfo: { wasSharing, contentType },
  }, `[SCREENSHARE DEBUG] screenshareHasEnded called — wasSharing=${wasSharing}, contentType=${contentType}`);

  if (wasSharing) {
    setIsSharing(false);
  }
  if (contentType === CONTENT_TYPE_CAMERA) {
    setCameraAsContentDeviceId('');
  }

  screenShareBridge.stop();
  logger.info({ logCode: 'screenshare_has_ended_complete' }, '[SCREENSHARE DEBUG] screenshareHasEnded complete — bridge.stop() called');
};

export const _handleStreamTermination = () => {
  screenshareHasEnded();
};

export const getMediaElement = () => document.getElementById(SCREENSHARE_MEDIA_ELEMENT_NAME);

export const getMediaElementDimensions = () => {
  const element = getMediaElement();
  return {
    width: element?.videoWidth ?? 0,
    height: element?.videoHeight ?? 0,
  };
};

export const setStreamEnabled = (enabled) => {
  screenShareBridge.setStreamEnabled(enabled);
};

export const setVolume = (volume) => {
  screenShareBridge.setVolume(volume);
};

export const getVolume = () => screenShareBridge.getVolume();

export const useShouldEnableVolumeControl = () => {
  const SCREENSHARE_CONFIG = window.meetingClientSettings.public.kurento.screenshare;
  const VOLUME_CONTROL_ENABLED = SCREENSHARE_CONFIG.enableVolumeControl;
  const hasAudio = useScreenshareHasAudio();

  return VOLUME_CONTROL_ENABLED && hasAudio;
};

export const useShowButtonForNonPresenters = () => {
  const MEDIA_CONFIG = window.meetingClientSettings.public.kurento;

  return MEDIA_CONFIG.screenshare.showButtonForNonPresenters;
};

export const attachLocalPreviewStream = (mediaElement) => {
  const { isTabletApp } = browserInfo;
  if (isTabletApp) {
    // We don't show preview for mobile app, as the stream is only available in native code
    return;
  }
  const stream = screenShareBridge.gdmStream;
  if (stream && mediaElement) {
    // Always muted, presenter preview.
    BridgeService.screenshareLoadAndPlayMediaStream(stream, mediaElement, true);
  }
};

export const setOutputDeviceId = (outputDeviceId) => {
  const screenShareElement = document.getElementById(SCREENSHARE_MEDIA_ELEMENT_NAME);
  const sinkIdSupported = screenShareElement && typeof screenShareElement.setSinkId === 'function';
  const srcStream = screenShareElement?.srcObject;

  if (typeof outputDeviceId === 'string'
    && sinkIdSupported
    && screenShareElement.sinkId !== outputDeviceId
    && srcStream
    && srcStream.getAudioTracks().length > 0) {
    try {
      screenShareElement.setSinkId(outputDeviceId);
      logger.debug({
        logCode: 'screenshare_output_device_change',
        extraInfo: {
          newDeviceId: outputDeviceId,
        },
      }, `Screenshare output device changed: to ${outputDeviceId || 'default'}`);
    } catch (error) {
      logger.error({
        logCode: 'screenshare_output_device_change_failure',
        extraInfo: {
          errorName: error.name,
          errorMessage: error.message,
          newDeviceId: outputDeviceId,
        },
      }, `Error changing screenshare output device - {${error.name}: ${error.message}}`);
    }
  }
};

export const shareScreen = async (
  isCameraAsContentBroadcasting,
  stopWatching,
  isPresenter,
  onFail,
  options = {},
) => {
  logger.info({
    logCode: 'screenshare_share_screen_called',
    extraInfo: {
      isCameraAsContentBroadcasting,
      isPresenter,
      hasStream: !!options.stream,
      hasConstraints: !!options.constraints,
    },
  }, `[SCREENSHARE DEBUG] shareScreen() called — isPresenter=${isPresenter}, cameraAsContent=${isCameraAsContentBroadcasting}`);

  if (isCameraAsContentBroadcasting) {
    logger.info({ logCode: 'screenshare_ending_camera_as_content' }, '[SCREENSHARE DEBUG] Ending camera-as-content before starting screenshare');
    screenshareHasEnded();
  }

  try {
    let stream;
    let contentType = CONTENT_TYPE_SCREENSHARE;
    if (options.stream == null) {
      // Pass custom constraints to BridgeService if provided
      const customConstraints = options.constraints || null;
      logger.info({
        logCode: 'screenshare_getting_screen_stream',
        extraInfo: { hasCustomConstraints: !!customConstraints },
      }, '[SCREENSHARE DEBUG] Calling BridgeService.getScreenStream (user will see browser picker)');
      stream = await BridgeService.getScreenStream(customConstraints);
      logger.info({
        logCode: 'screenshare_got_screen_stream',
        extraInfo: {
          streamActive: stream?.active,
          videoTracks: stream?.getVideoTracks()?.length,
          audioTracks: stream?.getAudioTracks()?.length,
        },
      }, `[SCREENSHARE DEBUG] Got screen stream — active=${stream?.active}, videoTracks=${stream?.getVideoTracks()?.length}, audioTracks=${stream?.getAudioTracks()?.length}`);

      // [RECORDING DEBUG] Deep inspection of video track for blank recording diagnosis
      if (stream) {
        const videoTracks = stream.getVideoTracks();
        const audioTracks = stream.getAudioTracks();
        videoTracks.forEach((track, idx) => {
          const settings = track.getSettings();
          const constraints = track.getConstraints();
          logger.info({
            logCode: 'screenshare_recording_debug_video_track',
            extraInfo: {
              trackIndex: idx,
              trackId: track.id,
              trackLabel: track.label,
              trackKind: track.kind,
              trackReadyState: track.readyState,
              trackEnabled: track.enabled,
              trackMuted: track.muted,
              // Settings tell us what's actually being captured
              settingsWidth: settings.width,
              settingsHeight: settings.height,
              settingsFrameRate: settings.frameRate,
              settingsDisplaySurface: settings.displaySurface,
              settingsCursor: settings.cursor,
              settingsDeviceId: settings.deviceId,
              // Constraints tell us what was requested
              constraints: JSON.stringify(constraints),
            },
          }, `[RECORDING DEBUG] Video track[${idx}]: label="${track.label}", readyState=${track.readyState}, enabled=${track.enabled}, muted=${track.muted}, resolution=${settings.width}x${settings.height}@${settings.frameRate}fps, displaySurface=${settings.displaySurface}`);

          // CRITICAL WARNINGS for blank recording scenarios
          if (!track.enabled) {
            logger.error({ logCode: 'screenshare_recording_debug_track_disabled' },
              `[RECORDING DEBUG] *** CRITICAL: Video track is DISABLED — recording will be BLANK ***`);
          }
          if (track.muted) {
            logger.warn({ logCode: 'screenshare_recording_debug_track_muted' },
              `[RECORDING DEBUG] *** WARNING: Video track is MUTED — may cause blank recording ***`);
          }
          if (track.readyState !== 'live') {
            logger.error({ logCode: 'screenshare_recording_debug_track_not_live' },
              `[RECORDING DEBUG] *** CRITICAL: Video track readyState="${track.readyState}" (not "live") — recording will be BLANK ***`);
          }
          if (settings.width === 0 || settings.height === 0) {
            logger.error({ logCode: 'screenshare_recording_debug_track_zero_res' },
              `[RECORDING DEBUG] *** CRITICAL: Video track has ${settings.width}x${settings.height} resolution — recording will be BLANK ***`);
          }

          // Monitor track for state changes during recording
          track.addEventListener('ended', () => {
            logger.warn({ logCode: 'screenshare_recording_debug_track_ended' },
              `[RECORDING DEBUG] Video track ENDED during session — recording may be truncated or partially blank`);
          });
          track.addEventListener('mute', () => {
            logger.warn({ logCode: 'screenshare_recording_debug_track_muted_event' },
              `[RECORDING DEBUG] Video track became MUTED during session — frames may stop flowing`);
          });
          track.addEventListener('unmute', () => {
            logger.info({ logCode: 'screenshare_recording_debug_track_unmuted_event' },
              `[RECORDING DEBUG] Video track UNMUTED during session — frames resumed`);
          });
        });

        if (videoTracks.length === 0) {
          logger.error({ logCode: 'screenshare_recording_debug_no_video_tracks' },
            `[RECORDING DEBUG] *** CRITICAL: Stream has NO video tracks — recording will be completely blank ***`);
        }

        audioTracks.forEach((track, idx) => {
          const settings = track.getSettings();
          logger.info({
            logCode: 'screenshare_recording_debug_audio_track',
            extraInfo: {
              trackIndex: idx,
              trackLabel: track.label,
              trackReadyState: track.readyState,
              trackEnabled: track.enabled,
              trackMuted: track.muted,
              sampleRate: settings.sampleRate,
              channelCount: settings.channelCount,
            },
          }, `[RECORDING DEBUG] Audio track[${idx}]: label="${track.label}", readyState=${track.readyState}, enabled=${track.enabled}`);
        });

        logger.info({ logCode: 'screenshare_recording_debug_stream_summary' },
          `[RECORDING DEBUG] Stream summary: active=${stream.active}, id=${stream.id}, videoTracks=${videoTracks.length}, audioTracks=${audioTracks.length}`);
      }

    } else {
      contentType = CONTENT_TYPE_CAMERA;
      stream = options.stream;
      logger.info({ logCode: 'screenshare_using_provided_stream' }, '[SCREENSHARE DEBUG] Using provided stream (camera as content)');
    }
    _trackStreamTermination(stream, _handleStreamTermination);

    if (!isPresenter) {
      logger.info({ logCode: 'screenshare_not_presenter_stopping' }, '[SCREENSHARE DEBUG] Not presenter — stopping tracks');
      MediaStreamUtils.stopMediaStreamTracks(stream);
      return;
    }

    logger.info({
      logCode: 'screenshare_calling_bridge_share',
      extraInfo: { contentType, bridgeName: screenShareBridge?.bridgeName },
    }, `[SCREENSHARE DEBUG] Calling bridge.share() — contentType=${contentType}, bridge=${screenShareBridge?.bridgeName}`);
    await screenShareBridge.share(stream, onFail, contentType);
    logger.info({ logCode: 'screenshare_bridge_share_returned' }, '[SCREENSHARE DEBUG] bridge.share() returned successfully');

    // [RECORDING DEBUG] Post-bridge health check — verify stream is still alive after SFU handshake
    if (stream) {
      const postShareVideoTracks = stream.getVideoTracks();
      postShareVideoTracks.forEach((track, idx) => {
        const settings = track.getSettings();
        logger.info({
          logCode: 'screenshare_recording_debug_post_bridge',
          extraInfo: {
            trackIndex: idx,
            readyState: track.readyState,
            enabled: track.enabled,
            muted: track.muted,
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
          },
        }, `[RECORDING DEBUG] Post-bridge video track[${idx}]: readyState=${track.readyState}, enabled=${track.enabled}, muted=${track.muted}, ${settings.width}x${settings.height}@${settings.frameRate}fps`);

        if (track.readyState !== 'live' || !track.enabled) {
          logger.error({ logCode: 'screenshare_recording_debug_post_bridge_dead' },
            `[RECORDING DEBUG] *** CRITICAL: Video track is ${track.readyState}/${track.enabled ? 'enabled' : 'disabled'} AFTER bridge.share() — Kurento will record a blank stream ***`);
        }
      });
    }

    // Stream might have been disabled in the meantime. I love badly designed
    // async components like this screen sharing bridge :) - prlanzarin 09 May 22
    if (!_isStreamActive(stream)) {
      logger.warn({ logCode: 'screenshare_stream_inactive_after_share' }, '[SCREENSHARE DEBUG] Stream became inactive after share — terminating');
      _handleStreamTermination();
      return;
    }

    // stop external video share if running
    stopWatching();

    setSharingContentType(contentType);
    setIsSharing(true);
    logger.info({ logCode: 'screenshare_share_screen_success' }, '[SCREENSHARE DEBUG] shareScreen() completed successfully — isSharing=true');
  } catch (error) {
    logger.error({
      logCode: 'screenshare_share_screen_error',
      extraInfo: { errorName: error?.name, errorMessage: error?.message },
    }, `[SCREENSHARE DEBUG] shareScreen() caught error: ${error?.name} — ${error?.message}`);
    onFail(error);
  }
};

export const viewScreenshare = (streamId, hasAudio, options = {}) => {
  logger.info({
    logCode: 'screenshare_viewer_starting',
    extraInfo: {
      streamId,
      hasAudio,
      outputDeviceId: options.outputDeviceId,
    },
  }, `[SCREENSHARE VIEWER] Starting to view screenshare - streamId=${streamId}, hasAudio=${hasAudio}`);

  screenShareBridge.view(streamId, { hasAudio, outputDeviceId: options.outputDeviceId })
    .then(() => {
      logger.info({
        logCode: 'screenshare_view_success',
        extraInfo: { streamId },
      }, `[SCREENSHARE VIEWER] View screenshare bridge call successful - streamId=${streamId}`);
    })
    .catch((error) => {
      logger.error({
        logCode: 'screenshare_view_failed',
        extraInfo: {
          streamId,
          errorName: error.name,
          errorMessage: error.message,
        },
      }, `[SCREENSHARE VIEWER] View screenshare failure - ${error.message}`);
    });
};

export const screenshareHasStarted = (streamId, hasAudio, isPresenter, options = {}) => {
  logger.info({
    logCode: 'screenshare_has_started',
    extraInfo: {
      streamId,
      hasAudio,
      isPresenter,
      outputDeviceId: options.outputDeviceId,
    },
  }, `[SCREENSHARE VIEWER] screenshareHasStarted called - isPresenter=${isPresenter}, streamId=${streamId}`);

  // Presenter's screen preview is local, so skip
  if (!isPresenter) {
    logger.info({
      logCode: 'screenshare_viewer_will_view',
    }, '[SCREENSHARE VIEWER] Not presenter, will call viewScreenshare');
    viewScreenshare(streamId, hasAudio, { outputDeviceId: options.outputDeviceId });
  } else {
    logger.info({
      logCode: 'screenshare_presenter_skip_view',
    }, '[SCREENSHARE VIEWER] Is presenter, skipping viewScreenshare (local preview)');
  }
};

export const screenShareEndAlert = () => AudioService
  .playAlertSound(`${window.meetingClientSettings.public.app.cdn
    + window.meetingClientSettings.public.app.basename}`
    + '/resources/sounds/ScreenshareOff.mp3');

/**
   * Get stats about all active screenshare peers.
   *
   * For more information see:
   *  - https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/getStats
   *  - https://developer.mozilla.org/en-US/docs/Web/API/RTCStatsReport

   * @param {Array[String]} statsType - An array containing valid RTCStatsType
   *                                    values to include in the return object
   *
   * @returns {Object} The information about each active screen sharing peer.
   *          The returned format follows the format returned by video's service
   *          getStats, which considers more than one peer connection to be returned.
   *          The format is given by:
   *          {
   *            peerIdString: RTCStatsReport
   *          }
   */
export const getStats = async (statsTypes = DEFAULT_SCREENSHARE_STATS_TYPES) => {
  const screenshareStats = {};
  let stats = null;

  if (typeof screenShareBridge.getStats === 'function') {
    stats = await screenShareBridge.getStats();
  } else {
    const peer = screenShareBridge.getPeerConnection();

    if (!peer) return null;

    stats = await peer.getStats();
  }

  if (!stats) return null;

  stats.forEach((stat) => {
    if (statsTypes.includes(stat.type) && (!stat.kind || stat.kind === 'video')) {
      screenshareStats[stat.type] = stat;
    }
  });

  return { screenshareStats };
};

// This method may throw errors
export const isMediaFlowing = (previousStats, currentStats) => {
  const bpsData = ConnectionStatusService.calculateBitsPerSecond(
    currentStats?.screenshareStats,
    previousStats?.screenshareStats,
  );
  const bpsDataAggr = Object.values(bpsData)
    .reduce((sum, partialBpsData = 0) => sum + parseFloat(partialBpsData), 0);

  return bpsDataAggr > 0;
};

export default {
  SCREENSHARE_MEDIA_ELEMENT_NAME,
  isMediaFlowing,
  screenshareHasEnded,
  screenshareHasStarted,
  shareScreen,
  screenShareEndAlert,
  isSharing,
  setIsSharing,
  setSharingContentType,
  getSharingContentType,
  getMediaElement,
  getMediaElementDimensions,
  attachLocalPreviewStream,
  getStats,
  setVolume,
  getVolume,
  setCameraAsContentDeviceId,
  getCameraAsContentDeviceId,
  setBridge,
  setOutputDeviceId,
  useCameraAsContentDeviceIdType,
  useIsSharing,
  useSharingContentType,
  useIsScreenGloballyBroadcasting,
  useIsCameraAsContentGloballyBroadcasting,
  useShouldEnableVolumeControl,
  useShowButtonForNonPresenters,
  useIsScreenBroadcasting,
  useIsCameraAsContentBroadcasting,
  useScreenshareHasAudio,
  useBroadcastContentType,
  useScreenshareStreamId,
};
